<template>
    <div class="shareLinkCard">
        <div class="shareLinkCard_header">
            <div class="shareLinkCard_info">
                <div class="shareLinkCard_titleRow">
                    <div class="shareLinkCard_status" :class="`shareLinkCard_status--${item.status}`">
                        {{ getStatusText(item.status) }}
                    </div>
                    <div class="shareLinkCard_title">{{ item.title }}</div>
                </div>
                <div class="shareLinkCard_subtitle">{{ item.description }}</div>
            </div>
            <div class="shareLinkCard_headerRight">
                <div class="shareLinkCard_actions">
                    <sw-tooltip content="Visualizar">
                        <sw-button
                            icon="earth"
                            circle-icon
                            size="sm"
                            hover-color="blue"
                            @click="$emit('view', item)"
                        />
                    </sw-tooltip>
                    <sw-tooltip content="Editar">
                        <sw-button
                            icon="pencil"
                            circle-icon
                            size="sm"
                            hover-color="orange"
                            @click="$emit('edit', item)"
                        />
                    </sw-tooltip>
                    <sw-tooltip content="Excluir">
                        <sw-button
                            icon="bin"
                            circle-icon
                            size="sm"
                            hover-color="red"
                            @click="$emit('delete', item)"
                        />
                    </sw-tooltip>
                </div>
            </div>
        </div>

        <div class="shareLinkCard_body">
            <div class="shareLinkCard_details">
                <div class="shareLinkCard_detail">
                    <label>Link de Compartilhamento:</label>
                    <div class="shareLinkCard_link">
                        <input
                            :value="item.shareUrl"
                            readonly
                            class="shareLinkCard_linkInput"
                            :id="`shareLink-${item.id}`"
                        />
                        <CopyButton :textToCopy="item.shareUrl" />
                    </div>
                </div>

                <div class="shareLinkCard_stats">
                    <div class="shareLinkCard_stat">
                        <sw-icon name="eye" />
                        <span>{{ item.stats.views }} visualizações</span>
                    </div>
                    <div class="shareLinkCard_stat">
                        <sw-icon name="download" />
                        <span>{{ item.stats.downloads }} downloads</span>
                    </div>
                    <div class="shareLinkCard_stat shareLinkCard_stat--auth" :class="{ '--protected': item.settings?.requireAuth }">
                        <sw-icon :name="item.settings?.requireAuth ? 'shield' : 'users'" />
                        <span>{{ item.settings?.requireAuth ? 'Acesso Protegido' : 'Acesso Público' }}</span>
                    </div>
                    <div class="shareLinkCard_stat">
                        <sw-icon name="calendar" />
                        <span>Criado em {{ formatDate(item.createdAt) }}</span>
                    </div>
                    <div v-if="item.expiresAt" class="shareLinkCard_stat">
                        <sw-icon name="clock" />
                        <span>Expira em {{ formatDate(item.expiresAt) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import CopyButton from '@Component/CopyButton.vue';

    import {
        WEB_LINK_STATUS_LABELS
    } from '@/constants';

    const props = defineProps({
        item: {
            type: Object,
            required: true
        }
    });

    const emit = defineEmits(['view', 'edit', 'delete']);

    // Methods
    const getStatusText = (status) => {
        return WEB_LINK_STATUS_LABELS[status] || 'Desconhecido';
    };

    const formatDate = (dateString) => {
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(dateString));
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    /* ShareLink Card Styles */
    .shareLinkCard {
        background: linear-gradient(135deg, var(--backgroundCard), #ffffff);
        border-radius: 0.875em;
        padding: 1.25em;
        border: 1px solid var(--borderColorLight, #e5e7eb);
        transition: all 0.3s ease;
        box-shadow: var(--boxShadowSm);
        margin-bottom: 0.875em;
        height: 100%;
        display: flex;
        flex-direction: column;

        &:hover {
            transform: translateY(-2px);
            box-shadow: var(--boxShadowLg);
            border-color: var(--colorBlue, #3b82f6);
        }
    }

    .shareLinkCard_header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 1.25em;
        padding-bottom: 0.875em;
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
    }

    .shareLinkCard_info {
        flex: 1;
    }

    .shareLinkCard_titleRow {
        display: flex;
        align-items: center;
        gap: 1em;
        margin-bottom: 0.25em;
    }

    .shareLinkCard_headerRight {
        display: flex;
        align-items: center;
        gap: 1.25em;
        flex-shrink: 0;
    }

    .shareLinkCard_title {
        font-size: 1.125em;
        font-weight: 700;
        color: var(--textPrimary);
        margin-bottom: 0;
    }

    .shareLinkCard_subtitle {
        font-size: 0.875em;
        color: var(--textSecondary);
        line-height: 1.4;
    }

    .shareLinkCard_status {
        padding: 0.375em 0.875em;
        border-radius: 0.5em;
        font-size: 0.8125em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.025em;

        &--active {
            background: rgba(16, 185, 129, 0.1);
            color: var(--colorGreen, #10b981);
            border: 1px solid rgba(16, 185, 129, 0.3);
        }

        &--inactive {
            background: rgba(107, 114, 128, 0.1);
            color: var(--colorGray, #6b7280);
            border: 1px solid rgba(107, 114, 128, 0.3);
        }

        &--expired {
            background: rgba(239, 68, 68, 0.1);
            color: var(--colorRed, #ef4444);
            border: 1px solid rgba(239, 68, 68, 0.3);
        }
    }

    .shareLinkCard_body {
        width: 100%;
        flex: 1;
    }

    .shareLinkCard_details {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 1.25em;
    }

    .shareLinkCard_detail {
        label {
            display: block;
            font-size: 0.8125em;
            font-weight: 600;
            color: var(--textSecondary);
            margin-bottom: 0.5em;
            text-transform: uppercase;
            letter-spacing: 0.025em;
        }
    }

    .shareLinkCard_link {
        display: flex;
        gap: 0.75em;
        align-items: center;
    }

    .shareLinkCard_linkInput {
        flex: 1;
        padding: 0.625em 1em;
        border: 1px solid var(--borderColorLight, #e5e7eb);
        border-radius: 0.5em;
        background: var(--backgroundInsideBox, #f8fafc);
        color: var(--textPrimary);
        font-size: 0.875em;
        font-family: monospace;

        &:focus {
            outline: none;
            border-color: var(--colorBlue, #3b82f6);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
    }

    .shareLinkCard_stats {
        display: flex;
        gap: 1.25em;
        flex-wrap: wrap;
    }

    .shareLinkCard_stat {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 0.8125em;
        color: var(--textSecondary);

        .sw-icon {
            font-size: 1em;
            color: var(--colorBlue, #3b82f6);
        }

        &--auth {
            .sw-icon {
                color: var(--colorGreen, #10b981);
            }

            &.--protected {
                .sw-icon {
                    color: var(--colorOrange, #f59e0b);
                }
            }
        }
    }

    .shareLinkCard_actions {
        display: flex;
        flex-direction: row;
        gap: 0.5em;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }

    /* Responsividade - Tablet */
    @include screen_medias(m) {
        .shareLinkCard {
            padding: 1.125em;
        }

        .shareLinkCard_header {
            flex-direction: column;
            gap: 1em;
            align-items: stretch;
        }

        .shareLinkCard_headerRight {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 1em;
        }

        .shareLinkCard_actions {
            flex-direction: row;
            gap: 0.5em;
            justify-content: flex-end;
            flex-shrink: 0;
        }

        .shareLinkCard_stats {
            gap: 1em;
            flex-wrap: wrap;
        }
    }

    /* Responsividade - Mobile */
    @include screen_medias(s) {
        .shareLinkCard {
            padding: 1em;
        }

        .shareLinkCard_header {
            flex-direction: column;
            gap: 1em;
            align-items: stretch;
        }

        .shareLinkCard_headerRight {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75em;
        }

        .shareLinkCard_actions {
            flex-direction: row;
            gap: 0.5em;
            justify-content: center;
            flex-wrap: wrap;
            width: 100%;

            .sw-tooltip {
                flex: 1;
                min-width: 0;

                .sw-button {
                    width: 100%;
                    min-width: 2.5em;
                }
            }
        }

        .shareLinkCard_stats {
            gap: 0.75em;
            flex-wrap: wrap;
        }

        .shareLinkCard_stat {
            font-size: 0.75em;
            flex: 1;
            min-width: fit-content;
        }
    }

    /* Responsividade extra para telas muito pequenas */
    @include screen_s {
        .shareLinkCard_link {
            flex-direction: column;
            gap: 0.5em;
            align-items: stretch;
        }

        .shareLinkCard_linkInput {
            width: 100%;
        }

        .shareLinkCard_titleRow {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75em;
            text-align: center;
        }

        .shareLinkCard_status {
            align-self: center;
            width: fit-content;
            margin: 0 auto;
        }

        .shareLinkCard_actions {
            gap: 0.375em;

            .sw-tooltip {
                .sw-button {
                    min-width: 3em;
                }
            }
        }
    }
</style>